import { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import { getValidToken } from '../utils/auth';
import { baseChartOptions } from '../utils/chartUtils';

const API_URL = process.env.REACT_APP_API_URL || '';
const CHART_HEIGHT = 300;

// Parse OCC symbol -> human-readable header, same idea as
// ContractChartWidget's parseContractSymbol but only what the header needs.
function parseContractSymbol(symbol) {
  const m = symbol?.match(/^([A-Z.]+)(\d{6})([CP])(\d{8})$/);
  if (!m) return null;
  const [, underlying, yymmdd, cp, strikeRaw] = m;
  const yy = yymmdd.slice(0, 2), mm = yymmdd.slice(2, 4), dd = yymmdd.slice(4, 6);
  return {
    underlying, expiry: `${mm}/${dd}/20${yy}`,
    type: cp === 'C' ? 'CALL' : 'PUT', strike: parseInt(strikeRaw, 10) / 1000,
  };
}

// Lightweight, read-only chart for a single expired backtest contract's
// cached 1-min bars - no polling, no timeframe switching, no live-price
// synthesis (the contract's day is already over, there's nothing "live").
// Deliberately much smaller than ContractChartWidget, which is built for
// live contracts (polls for live price, fetches "last N days ending now")
// and isn't a fit for an already-expired contract from an arbitrary past
// backtest day.
export default function BacktestContractChart({ variant, contract }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const mountedRef = useRef(true);
  const [error, setError] = useState(null);
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    const opts = baseChartOptions(containerRef.current.clientWidth || 300);
    opts.height = CHART_HEIGHT;
    const chart = createChart(containerRef.current, opts);
    chartRef.current = chart;
    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a', downColor: '#ef5350',
      borderUpColor: '#26a69a', borderDownColor: '#ef5350',
      wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });
    seriesRef.current = series;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w > 0 && mountedRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: w });
      }
    });
    ro.observe(containerRef.current);

    return () => {
      mountedRef.current = false;
      ro.disconnect();
      chart.remove();
      chartRef.current = seriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!contract?.symbol || !contract?.date || !seriesRef.current) {
      seriesRef.current?.setData([]);
      setEmpty(false);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        const token = await getValidToken();
        const res = await fetch(
          `${API_URL}/api/cheap-backtest/contract-bars?variant=${variant}&date=${contract.date}&symbol=${encodeURIComponent(contract.symbol)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const bars = await res.json();
        if (cancelled || !mountedRef.current || !seriesRef.current) return;
        seriesRef.current.setData(bars || []);
        setEmpty(!bars || bars.length === 0);
        if (bars && bars.length) chartRef.current?.timeScale().fitContent();
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [variant, contract?.symbol, contract?.date]);

  const parsed = parseContractSymbol(contract?.symbol);

  return (
    <div className="bg-gray-900 rounded-2xl p-4 mb-3 border" style={{ borderColor: '#1f2937' }}>
      <div className="text-xs text-gray-400 mb-2 font-mono">
        {contract?.symbol ? (
          parsed
            ? `${contract.symbol} · ${parsed.type} ${parsed.strike % 1 === 0 ? parsed.strike.toFixed(0) : parsed.strike.toFixed(1)} · ${parsed.expiry}`
            : contract.symbol
        ) : (
          <span className="text-gray-600">Click a row below to see its chart</span>
        )}
      </div>
      {error && <div className="text-xs text-red-400 mb-2">Failed to load chart: {error}</div>}
      {empty && !error && <div className="text-xs text-gray-600 mb-2">No cached bars for this contract/day</div>}
      <div ref={containerRef} style={{ width: '100%', height: CHART_HEIGHT }} />
    </div>
  );
}
